import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pager, QuickFilters } from "./QuickFilters";

const VIEWS = [["", "All"], ["most_viewed", "Most viewed"], ["most_journaled", "Most journaled"], ["ot", "Old Testament"], ["nt", "New Testament"], ["recent", "Recently edited"], ["incomplete", "Study incomplete"], ["no_scene", "No scene"], ["needs_type", "Needs type review"], ["import_unpublished", "Unpublished by import"], ["import_created", "Added by import"]].map(([key, label]) => ({ key, label }));
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Search, X, Eye, EyeOff, ExternalLink, ShieldCheck } from "lucide-react";
import { ActivityFilters, EMPTY_ACTIVITY } from "./ActivityFilters";
import { BulkBar } from "./BulkBar";
import { Checkbox } from "@/components/ui/checkbox";
import { sourceLabel, when } from "./QuestionActivity";
import { Link, useSearchParams } from "react-router-dom";
import QuestionEditor from "@/components/admin/QuestionEditor";

export default function AdminQuestions() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState("");
  const [book, setBook] = useState("");
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(() => params.get("edit")); // null | "new" | id
  const closeEditor = () => {
    setEditing(null);
    if (params.has("edit")) { const next = new URLSearchParams(params); next.delete("edit"); setParams(next, { replace: true }); }
  };
  const [act, setAct] = useState(EMPTY_ACTIVITY);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const PAGE = 50;
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(0); setSelected(new Set()); }, [debounced, status, view, book, act]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-questions", debounced, status, view, book, act, page],
    queryFn: async () => (await api.get("/admin/questions", { params: { search: debounced || undefined, status: status || undefined, view: view || undefined, book: book || undefined,
      ...Object.fromEntries(Object.entries(act).filter(([, v]) => v)), skip: page * PAGE, limit: PAGE } })).data,
    placeholderData: keepPreviousData,
  });
  const pages = data ? Math.max(1, Math.ceil(data.count / PAGE)) : 1;

  const setStatusOf = async (q, next) => {
    try {
      await api.put(`/admin/questions/${q.id}/status`, { status: next });
      toast.success(next === "published" ? "Published — now visible on the site." : "Moved to drafts — hidden from the site.");
      invalidate();
      qc.invalidateQueries({ queryKey: ["question", q.id] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const listParams = () => ({ search: debounced || undefined, status: status || undefined, view: view || undefined, book: book || undefined,
    ...Object.fromEntries(Object.entries(act).filter(([, v]) => v)) });
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pageIds = data?.questions.map((q) => q.id) || [];
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () => setSelected((s) => { const n = new Set(s); pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id))); return n; });
  const selectAllMatching = async () => {
    const r = (await api.get("/admin/questions", { params: { ...listParams(), ids_only: true } })).data;
    setSelected(new Set(r.ids));
  };
  const runBulk = async (action) => {
    setBulkBusy(true);
    try {
      await api.post("/admin/questions/bulk", { ids: [...selected], action });
      toast.success(`${action === "delete" ? "Deleted" : action === "publish" ? "Republished" : "Unpublished"} ${selected.size} question${selected.size === 1 ? "" : "s"}.`);
      setSelected(new Set());
      invalidate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-questions"] });
    qc.invalidateQueries({ queryKey: ["admin-overview"] });
    qc.invalidateQueries({ queryKey: ["questions"] });
    qc.invalidateQueries({ queryKey: ["meta"] });
    qc.invalidateQueries({ queryKey: ["types"] });
    qc.invalidateQueries({ queryKey: ["admin-activity"] });
  };

  if (editing) {
    return (
      <QuestionEditor
        id={editing === "new" ? null : editing}
        onClose={closeEditor}
        onSaved={() => { invalidate(); closeEditor(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-questions">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, question, voice, type…" className="rounded-full pl-9 pr-9" data-testid="admin-question-search" />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setDebounced(""); }} aria-label="Clear search" title="Clear search" className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" data-testid="admin-question-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select value={book} onChange={(e) => setBook(e.target.value)} className="h-10 max-w-[180px] rounded-full border border-input bg-background px-3 text-sm" data-testid="admin-book-filter">
          <option value="">All books</option>
          {(data?.books || []).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-full border border-input bg-background px-3 text-sm" data-testid="admin-status-filter">
          <option value="">All</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </select>
        <Button onClick={() => setEditing("new")} className="rounded-full bg-primary text-primary-foreground" data-testid="admin-new-question">
          <Plus className="mr-2 h-4 w-4" /> New question
        </Button>
      </div>
      <QuickFilters options={VIEWS} value={view} onChange={setView} testId="question-filter" />
      <ActivityFilters value={act} onChange={setAct} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={allOnPage} onCheckedChange={togglePage} data-testid="bulk-select-page" aria-label="Select all on this page" /> Select page
        </label>
        <p className="font-mono text-xs text-muted-foreground" data-testid="admin-question-count">
          {data ? `${data.count} question${data.count === 1 ? "" : "s"} · showing ${data.count ? page * PAGE + 1 : 0}–${Math.min((page + 1) * PAGE, data.count)}` : " "}{isFetching && !isLoading ? " · updating…" : ""}
        </p>
        <Pager page={page} pages={pages} onChange={setPage} />
      </div>
      {selected.size > 0 && <BulkBar count={selected.size} total={data?.count || 0} onSelectAll={selectAllMatching} onClear={() => setSelected(new Set())} onAction={runBulk} busy={bulkBusy} />}
      {isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card" data-testid="admin-question-list">
          {data?.questions.map((q) => (
            <li key={q.id} onClick={() => setEditing(q.id)} className="flex cursor-pointer flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40 sm:flex-nowrap" data-testid={`admin-row-${q.id}`}>
              <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                <Checkbox checked={selected.has(q.id)} onCheckedChange={() => toggleOne(q.id)} data-testid={`bulk-select-${q.id}`} aria-label={`Select ${q.reference}`} />
              </span>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => setEditing(q.id)} className="block w-full truncate text-left font-serif text-lg text-foreground transition-colors hover:text-primary hover:underline" title="Edit this question" data-testid={`admin-open-${q.id}`}>&ldquo;{q.question_kjv}&rdquo;</button>
                <p className="mt-0.5 flex flex-wrap gap-x-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  {q.status === "draft" && <span className="rounded-full bg-amber-500/15 px-2 text-amber-700 dark:text-amber-300" data-testid={`admin-draft-badge-${q.id}`}>Draft</span>}
                  {q.import_created && <span className="rounded-full bg-sky-500/15 px-2 text-sky-700 dark:text-sky-300" title={`Added from ${q.import_created}`} data-testid={`admin-imported-badge-${q.id}`}>Imported</span>}
                  <span>{q.reference}</span><span>· {q.who_asked.join(", ")}</span><span>· {q.type}</span>
                  <span className={q.words ? "" : "text-destructive"}>· {q.words} words</span>
                  {q.metric != null && <span className="text-gold">· {q.metric} {view === "most_viewed" ? "views" : "entries"}</span>}
                  {!q.has_study && <span className="text-destructive">· study incomplete</span>}
                </p>
                <p className="mt-0.5 font-mono text-[10px] text-muted-foreground" data-testid={`admin-row-activity-${q.id}`}>
                  Created {when(q.created_at)} · {sourceLabel(q.created_source)} — Updated {when(q.last_change_at)} · {sourceLabel(q.last_change_source)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" title={q.status === "draft" ? "Publish" : "Unpublish (make draft)"} onClick={() => setStatusOf(q, q.status === "draft" ? "published" : "draft")} data-testid={`admin-toggle-status-${q.id}`}>
                  {q.status === "draft" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
                <Button asChild variant="ghost" size="icon" title="View on site"><a href={`/question/${q.id}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-view-${q.id}`}><ExternalLink className="h-4 w-4" /></a></Button>
                <Button asChild variant="ghost" size="icon" title="Review with accuracy check"><Link to={`/admin?tab=review&id=${q.id}`} data-testid={`admin-review-${q.id}`}><ShieldCheck className="h-4 w-4" /></Link></Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!isLoading && pages > 1 && <div className="flex justify-end"><Pager page={page} pages={pages} onChange={setPage} testId="admin-pager-bottom" /></div>}
    </div>
  );
}

